import { ContentfulProduct } from '@ravago/shared/page-data/models/contentful';
import { OverviewPageProductItem } from '@ravago/shared/page-data/models/elastic';
import { ProductCard } from '@ravago/shared/page-data/models/elements';

import { AssetUtils } from '../../lib/utils/asset/asset.utils';

export class ContentfulProductClientUtils {
  static transformToProductCard(assetBasePath: string, toTransform: ContentfulProduct | OverviewPageProductItem, acceptWebp?: boolean): ProductCard {
    const productCard: ProductCard = {
      id: `${toTransform.id}`,
      slug: toTransform.slug,
      name: toTransform.name,
      producer: '',
      polymerSubgroup: toTransform.polymerSubGroup ?? '',
      fillers: [],
      technologies: toTransform.processes?.map(({ name }) => name) ?? [],
      features: [],
      quality: '',
      isPreferred: toTransform.isPreferred,
      image: this.getProductCardImage(toTransform, assetBasePath, acceptWebp)
    };

    if ('qualifier' in toTransform) {
      productCard.producer = toTransform.producer;
      productCard.fillers = toTransform.fillers?.map(({ name, percentage }) => `${percentage !== undefined ? `${percentage}% ` : ''}${name}`) ?? [];
      productCard.features = toTransform.features ?? [];
      productCard.quality = toTransform.quality ?? '';
      productCard.compareId = `product-${toTransform.id}`;
      if (toTransform.sustainabilityGroup && toTransform.sustainabilityGroup.subSustainability && toTransform.sustainabilityGroup.subSustainability.icon) {
        productCard.sustainability = {
          label: toTransform.sustainabilityGroup.subSustainability.label,
          icon: toTransform.sustainabilityGroup.subSustainability.icon
        };
      }
    } else {
      productCard.producer = toTransform?.brand?.producer?.name ?? '';
      productCard.fillers = toTransform.fillers?.map(({ filler, percentage }) => `${percentage ? `${percentage} ` : ''}${filler.name}`) ?? [];
      productCard.features = toTransform.features?.map((feature) => feature.name) ?? [];
      productCard.quality = toTransform.quality?.name ?? '';
      productCard.compareId = toTransform.id;
      productCard.noFollow = toTransform.seoMetadata?.noFollow;
      if (toTransform.sustainabilitySubGroup && toTransform.sustainabilitySubGroup.labelIconPair && toTransform.sustainabilitySubGroup.labelIconPair.icon) {
        productCard.sustainability = {
          label: toTransform.sustainabilitySubGroup.labelIconPair.label,
          icon: toTransform.sustainabilitySubGroup.labelIconPair.icon
        };
      }
    }

    return productCard;
  }

  private static getProductImageFromOverviewProduct(product: OverviewPageProductItem) {
    if (product.imageUrl) return product.imageUrl;
    if (product.segments?.length) return product.segments[0].imageURL;
    if (!product.industries) return undefined;

    for (let i = 0; i < product.industries.length; i += 1) {
      const industry = product.industries[i];
      for (let j = 0; j < industry.segments.length; j += 1) {
        const segment = industry.segments[j];
        if (segment.imageURL) return segment.imageURL;
      }
    }

    return undefined;
  }

  private static getProductCardImage(product: ContentfulProduct | OverviewPageProductItem, assetBasePath: string, acceptWebp?: boolean): string {
    if ('qualifier' in product) {
      const imageUrl = this.getProductImageFromOverviewProduct(product);
      if (imageUrl) return AssetUtils.optimizeImageIfRequired(imageUrl, acceptWebp, 400);

      return `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;
    }

    if (product.image?.url) return product.image.url;

    if (product.segments.length && product.segments[0].image?.url) return AssetUtils.optimizeImageIfRequired(product.segments[0].image?.url, acceptWebp, 400);

    return `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;
  }
}
